import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'clsx';
import { useSelector } from 'react-redux';

import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { testId } from 'ui-components/services/testid-service';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { Input } from 'ui-components/components/input';
import { WeightInput } from 'ui-components/components/weight-input';
import { DimensionInput } from 'ui-components/components/dimension-input';
import { FlexRow } from 'ui-components/components/flex/row';

import Global from '../../../../store/global-selectors';
import { convertDimensions } from '../../../../services/unit-services/dimensions/dimension-convert-service';
import { Confirmations } from '../../../../enums/confirmation';
import { ConfirmationOption } from '../../../../fulfillment-planner/confirmation-option';
import { ShipFromContainer } from './ship-from/container';
import { ShipFromValidator } from './ship-from/validator';
import { ShipToCountry } from './ship-to-country';
import { PackageTypeContainer } from './package-type/container';
import { ServiceClassification } from './service-classification';
import { Scale } from '../../../../fulfillment-planner/weight/scale';
import { AU, NZ } from '../../../../constants/country-codes';
import { useEnableRateBrowserFixup } from '../../../../feature-flags/hooks/use-enable-rate-browser-fixup';
import { ShipFromLinkDropdown } from '../shipfrom-link-dropdown/shipfrom-link-dropdown';
import { useCountryConfig } from '../../../../hooks/use-country-config';
import { CountryConfigNames } from '../../../../contexts/country-config-context/country-config-enums';
import { mapRateBrowserFormToFreightSeed } from '../rate-browser-to-freight-seed';

import { messages, labelMessages } from './messages';
import style from './style.module.less';

export const RateBrowserConfigurationForm = ({
  confirmationOptionId: confirmationOptionIdProp,
  hasDimensionsError,
  isFromCSWRateBrowserClick = false,
  isShipToResidentialAddress: isShipToResidentialAddressProp,
  packageDimensions: packageDimensionsProp,
  packageId: packageIdProp,
  onBrowseFreightRates,
  onReadFromScale,
  onSubmit,
  sellerDefaultDimensionUnit,
  serviceClassificationId: serviceClassificationIdProp,
  setShouldUseScale,
  shipFromId: shipFromIdProp,
  shipFromPostalCode: shipFromPostalCodeProp = '',
  shipToCityOrSuburb: shipToCityOrSuburbProp,
  shipToCountryCode: shipToCountryCodeProp,
  shipToPostalCode: shipToPostalCodeProp,
  shouldUseScale,
  weight,
  selectedLabelProviderId = '',
  isMultiPackage,
  packageIds,
}) => {
  const [confirmationOptionId, setConfirmationOptionId] = useState(confirmationOptionIdProp);
  const [isShipToResidentialAddress, setIsShipToResidentialAddress] = useState(isShipToResidentialAddressProp);
  const [packageDimensions, setPackageDimensions] = useState(packageDimensionsProp);
  const [packageId, setPackageId] = useState(packageIdProp);
  const [serviceClassificationId, setServiceClassificationId] = useState(serviceClassificationIdProp);
  const [shipFromId, setShipFromId] = useState(shipFromIdProp);
  const [shipToCityOrSuburb, setShipToCityOrSuburb] = useState(shipToCityOrSuburbProp);
  const [shipToCountryCode, setShipToCountryCode] = useState(shipToCountryCodeProp);
  const [shipToPostalCode, setShipToPostalCode] = useState(shipToPostalCodeProp);
  const [weightValue, setWeightValue] = useState(weight.value);
  const [shipFromPostalCode, setShipFromPostalCode] = useState(shipFromPostalCodeProp);
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const defaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const intl = useIntl();
  const [, rateBrowserConfigurationData] = useCountryConfig(CountryConfigNames.RateBrowserConfiguration);

  const { showConfirmationSelector, showPackageSelector, showServiceClass, showCityFieldOnDomestic } = rateBrowserConfigurationData;

  useEffect(() => {
    const useUpdatedDimensions = packageDimensionsProp.width && packageDimensionsProp.height && packageDimensionsProp.length;
    if (shouldUseScale && useUpdatedDimensions) {
      setShouldUseScale(false);
      setPackageDimensions(packageDimensionsProp);
      setWeightValue(weight.value);
    }

    if (shouldUseScale && !useUpdatedDimensions) {
      setShouldUseScale(false);
      setWeightValue(weight.value);
    }
  }, [
    packageDimensionsProp,
    packageDimensionsProp.width,
    packageDimensionsProp.height,
    packageDimensionsProp.length,
    setShouldUseScale,
    shouldUseScale,
    weight.value,
  ]);

  const onIsShipToResidentialAddressFieldLabelClicked = useCallback(() => setIsShipToResidentialAddress((val) => !val), []);

  const onPackageDimensionsChanged = useCallback(
    (dimensions) => {
      const convertedPackageDimensions =
        dimensions.unit !== sellerDefaultDimensionUnit ? convertDimensions(dimensions, sellerDefaultDimensionUnit) : dimensions;
      convertedPackageDimensions.unit = convertedPackageDimensions.unit.toLowerCase();
      setPackageDimensions(convertedPackageDimensions);
    },
    [sellerDefaultDimensionUnit],
  );

  const onPackageTypeChanged = useCallback(
    (id, dimensions) => {
      setPackageId(id);
      onPackageDimensionsChanged(dimensions);
    },
    [onPackageDimensionsChanged],
  );

  const sellerShipFroms = useSelector(Global.selectSellerShipFroms);
  const selectShipFromDropdownZip = useCallback(
    (shipFrom) => {
      setShipFromId(shipFrom.shipFromId);
      const postalCode = sellerShipFroms.find((sf) => sf.shipFromId === shipFrom.shipFromId)?.postalCode;
      setShipFromPostalCode(shipFrom.postalCode || postalCode);
    },
    [sellerShipFroms],
  );

  const isRateBrowserFixupEnabled = useEnableRateBrowserFixup();
  const shouldShowFromPostalCode = isRateBrowserFixupEnabled && !isFromCSWRateBrowserClick;

  const onSubmitForm = useCallback(() => {
    const payload = {
      shipFromId: shipFromId || shipFromIdProp,
      shipFromPostalCode: shouldShowFromPostalCode ? shipFromPostalCode : '',
      shipToPostalCode,
      shipToCity: shipToCityOrSuburb,
      shipToCountryCode,
      isResidential: isShipToResidentialAddress,
      weight: {
        unit: weight.unit,
        value: weightValue,
      },
      packageId,
      dimensions: packageDimensions,
      confirmationType: confirmationOptionId,
      serviceClassificationId,
    };
    onSubmit(payload);
  }, [
    confirmationOptionId,
    isShipToResidentialAddress,
    onSubmit,
    packageDimensions,
    packageId,
    serviceClassificationId,
    shipFromId,
    shipFromIdProp,
    shipToCityOrSuburb,
    shipToCountryCode,
    shipToPostalCode,
    weight.unit,
    weightValue,
    shipFromPostalCode,
    shouldShowFromPostalCode,
  ]);

  const onBrowseFreightRatesClicked = useCallback(() => {
    const seed = mapRateBrowserFormToFreightSeed(
      {
        shipFromId: shipFromId || shipFromIdProp,
        shipToCityOrSuburb,
        shipToPostalCode,
        shipToCountryCode,
        isShipToResidentialAddress,
        weightValue,
        weightUnit: weight.unit,
        packageDimensions,
      },
      { defaultWeightUnit, defaultDimensionUnit: sellerDefaultDimensionUnit },
    );
    onBrowseFreightRates?.(seed);
  }, [
    defaultWeightUnit,
    isShipToResidentialAddress,
    onBrowseFreightRates,
    packageDimensions,
    sellerDefaultDimensionUnit,
    shipFromId,
    shipFromIdProp,
    shipToCityOrSuburb,
    shipToCountryCode,
    shipToPostalCode,
    weight.unit,
    weightValue,
  ]);

  const renderShipFrom = () => {
    return (
      <ShipFromValidator selectedLabelProviderId={selectedLabelProviderId} selectedShipFromId={shipFromId}>
        {({ validation }) => (
          <div className={style.field} {...testId('ship-from')}>
            <div
              className={classnames(style['section-header'], {
                [style.error]: validation.helperTextType === INLINE_MESSAGE_TYPES.error,
              })}
            >
              <FormattedMessage {...labelMessages.shipFrom} />
            </div>
            {!shouldShowFromPostalCode && (
              <ShipFromContainer onChange={setShipFromId} selectedShipFromId={shipFromId} validation={validation} />
            )}
            {shouldShowFromPostalCode && (
              <div className={style.field} {...testId('ship-to-postal-code')}>
                <FlexRow className={style['label-with-dropdown']}>
                  <div className={style['field-label']}>
                    <FormattedMessage {...labelMessages.postalCode} />
                  </div>
                  <ShipFromLinkDropdown onSelectShipFrom={selectShipFromDropdownZip} selectedShipFromId={shipFromId} />
                </FlexRow>
                <Input
                  className={style['text-input']}
                  maxLength={50}
                  onChange={setShipFromPostalCode}
                  type='text'
                  value={shipFromPostalCode}
                />
              </div>
            )}
          </div>
        )}
      </ShipFromValidator>
    );
  };

  const renderShipToSectionHeader = () => {
    return (
      <div className={style['section-header']} {...testId('ship-to-section-header')}>
        <FormattedMessage {...labelMessages.shipTo} />
      </div>
    );
  };

  const renderShipToCountry = () => {
    return (
      <div className={style.field} {...testId('ship-to-country')}>
        <div className={style['field-label']}>
          <FormattedMessage {...labelMessages.country} />
        </div>
        <ShipToCountry disabled={isFromCSWRateBrowserClick} onChange={setShipToCountryCode} selectedCountryCode={shipToCountryCode} />
      </div>
    );
  };

  const renderShipToPostalCode = () => {
    return (
      <div className={style.field} {...testId('ship-to-postal-code')}>
        <div className={style['field-label']}>
          <FormattedMessage {...labelMessages.postalCode} />
        </div>
        <Input
          className={style['text-input']}
          disabled={isFromCSWRateBrowserClick}
          maxLength={50}
          onChange={setShipToPostalCode}
          type='text'
          value={shipToPostalCode}
        />
      </div>
    );
  };

  const renderShipToCityOrSuburb = () => {
    const shouldHideCityOrSuburb = isRateBrowserFixupEnabled && shipToCountryCode === sellerHomeCountry && !showCityFieldOnDomestic;
    if (shouldHideCityOrSuburb) {
      return null;
    }
    const useSuburbForShipToCityLabel = [AU, NZ].includes(shipToCountryCode);
    return (
      <div className={style.field} {...testId('ship-to-city')}>
        <div className={style['field-label']}>
          {!useSuburbForShipToCityLabel && <FormattedMessage {...labelMessages.city} />}
          {useSuburbForShipToCityLabel && <FormattedMessage {...labelMessages.suburb} />}
        </div>
        <Input
          className={style['text-input']}
          disabled={isFromCSWRateBrowserClick}
          maxLength={50}
          onChange={setShipToCityOrSuburb}
          type='text'
          value={shipToCityOrSuburb}
        />
      </div>
    );
  };

  const renderShipToResidentialAddressIndicator = () => {
    const isDisabled = isFromCSWRateBrowserClick;
    return (
      <div className={classnames(style.field, style['flex-row'])} {...testId('ship-to-residential-address-indicator')}>
        <Checkbox checked={isShipToResidentialAddress} disabled={isDisabled} onChange={setIsShipToResidentialAddress} />
        {isDisabled ? (
          <span className={classnames(style['field-label'])}>
            <FormattedMessage {...labelMessages.residentialAddress} />
          </span>
        ) : (
          <span className={classnames(style['field-label'], style.clickable)} onClick={onIsShipToResidentialAddressFieldLabelClicked}>
            <FormattedMessage {...labelMessages.residentialAddress} />
          </span>
        )}
      </div>
    );
  };

  const renderShipToSection = () => {
    return (
      <div className={style.section} {...testId('ship-to-section')}>
        {renderShipToSectionHeader()}
        {renderShipToCountry()}
        {renderShipToCityOrSuburb()}
        {renderShipToPostalCode()}
        {renderShipToResidentialAddressIndicator()}
      </div>
    );
  };

  const renderWeight = () => {
    return (
      <div className={style.field} {...testId('weight')}>
        <div className={style['field-label']}>
          <FormattedMessage {...labelMessages.weight} />
        </div>
        <div className={style['flex-row']}>
          <WeightInput disabled={isMultiPackage} onChange={setWeightValue} unit={weight.unit} weight={weightValue} />
          <Scale onReadFromScale={onReadFromScale} packageCount={packageIds?.length || 0} />
        </div>
      </div>
    );
  };

  const renderPackageType = () => {
    return (
      <div className={style.field} {...testId('package-type')}>
        <div className={style['field-label']}>
          <FormattedMessage {...labelMessages.package} />
        </div>
        <PackageTypeContainer
          disabled={isMultiPackage}
          onChange={onPackageTypeChanged}
          packageIds={isMultiPackage ? packageIds : undefined}
          selectedPackageTypeId={packageId}
        />
      </div>
    );
  };

  const renderPackageDimensions = () => {
    const dimensions = {
      length: packageDimensions.length,
      width: packageDimensions.width,
      height: packageDimensions.height,
    };
    if (isMultiPackage) {
      dimensions.length = 0;
      dimensions.width = 0;
      dimensions.height = 0;
    }
    return (
      <div className={style.field} {...testId('package-dimensions')}>
        <div className={style['field-label']}>
          <FormattedMessage {...labelMessages.size} />
        </div>
        <DimensionInput
          dimensions={dimensions}
          disabled={isMultiPackage}
          inputClassName={classnames({
            [style['has-error']]: hasDimensionsError,
          })}
          onChange={onPackageDimensionsChanged}
          unit={packageDimensions.unit}
        />
      </div>
    );
  };

  const renderConfirmationType = () => {
    const confirmationOptions = Object.values(Confirmations).map(({ messageDescriptor }, ordinal) => ({
      confirmationOptionId: `${ordinal}`,
      name: intl.formatMessage(messageDescriptor),
    }));

    return (
      <div className={style.field} {...testId('confirmation-type')}>
        <div className={style['field-label']}>
          <FormattedMessage {...labelMessages.confirmation} />
        </div>
        <ConfirmationOption
          confirmationOptions={confirmationOptions}
          onChange={setConfirmationOptionId}
          selectedConfirmationOptionId={confirmationOptionId}
        />
      </div>
    );
  };

  const renderServiceClassification = () => {
    return (
      <div className={style.field} {...testId('service-classification')}>
        <div className={style['field-label']}>
          <FormattedMessage {...labelMessages.serviceClass} />
        </div>
        <ServiceClassification onChange={setServiceClassificationId} selectedServiceClassificationId={serviceClassificationId} />
      </div>
    );
  };

  const renderShipmentInformationSection = () => {
    return (
      <div className={style.section} {...testId('shipment-information-section')}>
        <div className={style['section-header']} {...testId('shipment-information-section-header')}>
          <FormattedMessage {...labelMessages.shipmentInformation} />
        </div>
        {renderWeight()}
        {showPackageSelector && renderPackageType()}
        {renderPackageDimensions()}
        {showConfirmationSelector && renderConfirmationType()}
        {showServiceClass && renderServiceClassification()}
      </div>
    );
  };

  const renderBody = () => {
    return (
      <div className={style.body} {...testId('body')}>
        <div className={style.section} {...testId('ship-from-section')}>
          {renderShipFrom()}
        </div>
        {renderShipToSection()}
        {renderShipmentInformationSection()}
      </div>
    );
  };

  return (
    <div className={style.form} {...testId('rate-browser-configuration-form')}>
      <div className={style.header} {...testId('header')}>
        <FormattedMessage {...messages.configureRates} />
        {onBrowseFreightRates && (
          <Button
            className={style['browse-freight-rates-link']}
            onClick={onBrowseFreightRatesClicked}
            testId='browse-freight-rates-link'
            theme='link'
          >
            <FormattedMessage {...messages.browseFreightRates} />
          </Button>
        )}
      </div>
      {renderBody()}
      <div className={style.footer} {...testId('footer')}>
        <Button onClick={onSubmitForm} testId='submit-button' theme='primary'>
          <FormattedMessage {...messages.browseRates} />
        </Button>
      </div>
    </div>
  );
};

RateBrowserConfigurationForm.propTypes = {
  confirmationOptionId: PropTypes.string.isRequired,
  hasDimensionsError: PropTypes.bool.isRequired,
  isShipToResidentialAddress: PropTypes.bool.isRequired,
  onReadFromScale: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  packageDimensions: PropTypes.shape({
    unit: PropTypes.string.isRequired,
    length: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  packageId: PropTypes.string.isRequired,
  sellerCurrencyCode: PropTypes.string.isRequired,
  sellerDefaultDimensionUnit: PropTypes.string.isRequired,
  serviceClassificationId: PropTypes.string.isRequired,
  shipFromId: PropTypes.string,
  shipFromPostalCode: PropTypes.string,
  shipToCityOrSuburb: PropTypes.string.isRequired,
  shipToCountryCode: PropTypes.string.isRequired,
  shipToPostalCode: PropTypes.string.isRequired,
  weight: PropTypes.shape({
    unit: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
  }).isRequired,
  weightScaleInformation: PropTypes.shape({
    value: PropTypes.number,
    requestId: PropTypes.string,
  }).isRequired,
  shouldUseScale: PropTypes.bool.isRequired,
  setShouldUseScale: PropTypes.func.isRequired,
  isFromCSWRateBrowserClick: PropTypes.bool,
  selectedLabelProviderId: PropTypes.string,
  isMultiPackage: PropTypes.bool,
  packageIds: PropTypes.arrayOf(PropTypes.string),
  onBrowseFreightRates: PropTypes.func,
};
